import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  Button,
  Input,
  Select,
  Card,
  CardBody,
  Badge,
  EmptyState,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@/components/ui';
import { templateApi, getErrorMessage } from '@/lib/api';
import type { PaymentType, RecurringFrequency } from '@/types';

const paymentTypeOptions = [
  { value: '', label: 'All Types' },
  { value: 'RESERVATION_FEE', label: 'Reservation Fee' },
  { value: 'DOWN_PAYMENT', label: 'Down Payment' },
  { value: 'MONTHLY_AMORTIZATION', label: 'Monthly Amortization' },
  { value: 'SPOT_CASH', label: 'Spot Cash' },
  { value: 'BALANCE_PAYMENT', label: 'Balance Payment' },
  { value: 'OTHER', label: 'Other' },
];

const paymentTypeSelectOptions = [
  { value: 'RESERVATION_FEE', label: 'Reservation Fee' },
  { value: 'DOWN_PAYMENT', label: 'Down Payment' },
  { value: 'MONTHLY_AMORTIZATION', label: 'Monthly Amortization' },
  { value: 'SPOT_CASH', label: 'Spot Cash' },
  { value: 'BALANCE_PAYMENT', label: 'Balance Payment' },
  { value: 'OTHER', label: 'Other' },
];

const currencyOptions = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'PHP', label: 'PHP - Philippine Peso' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
  { value: 'NZD', label: 'NZD - New Zealand Dollar' },
  { value: 'KRW', label: 'KRW - South Korean Won' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
  { value: 'IDR', label: 'IDR - Indonesian Rupiah' },
  { value: 'VND', label: 'VND - Vietnamese Dong' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'AED', label: 'AED - UAE Dirham' },
];

const locationOptions = [
  { value: '', label: 'No default' },
  { value: 'Niseko', label: 'Niseko' },
  { value: 'Davao', label: 'Davao' },
  { value: 'Madrid', label: 'Madrid' },
  { value: 'Libis', label: 'Libis' },
];

const recurringFrequencyOptions = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' },
];

interface LineItemTemplate {
  description: string;
  paymentType?: PaymentType;
  customPaymentType?: string;
  quantity?: number;
  unitPrice: number;
}

interface PaymentLinkTemplate {
  id: string;
  name: string;
  description?: string;
  title: string;
  linkDescription?: string;
  paymentType: PaymentType;
  customPaymentType?: string;
  amount?: number;
  currency: string;
  allowPartialPayment: boolean;
  minimumAmount?: number;
  isRecurring: boolean;
  recurringFrequency?: RecurringFrequency;
  recurringCount?: number;
  propertyLocation?: string;
  sendEmailOnCreate: boolean;
  sendReminderEmails: boolean;
  lineItems?: LineItemTemplate[];
  isActive: boolean;
  createdAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

type ModalMode = 'create' | 'edit' | null;

interface LineItemFormData {
  description: string;
  paymentType: string;
  customPaymentType: string;
  quantity: string;
  unitPrice: string;
  isRecurring: boolean;
  recurringFrequency: string;
  recurringCount: string;
}

const emptyLineItem: LineItemFormData = {
  description: '',
  paymentType: 'RESERVATION_FEE',
  customPaymentType: '',
  quantity: '1',
  unitPrice: '',
  isRecurring: false,
  recurringFrequency: '',
  recurringCount: '',
};

const initialFormData = {
  name: '',
  description: '',
  title: '',
  linkDescription: '',
  currency: 'PHP',
  allowPartialPayment: false,
  minimumAmount: '',
  propertyLocation: '',
  sendEmailOnCreate: true,
  sendReminderEmails: true,
};

export function TemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PaymentLinkTemplate | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{ ...emptyLineItem }]);
  const [expandedItems, setExpandedItems] = useState<number[]>([0]);
  const [deleteTemplate, setDeleteTemplate] = useState<PaymentLinkTemplate | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['templates', { search, paymentTypeFilter, page }],
    queryFn: async () => {
      const response = await templateApi.list({
        search: search || undefined,
        paymentType: paymentTypeFilter || undefined,
        page,
        limit: 20,
      });
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: { formData: typeof initialFormData; lineItems: LineItemFormData[] }) => {
      // Get first recurring item for main recurring settings
      const firstRecurringItem = data.lineItems.find(item => item.isRecurring);
      const mainPaymentType = data.lineItems[0]?.paymentType || 'OTHER';
      const mainCustomPaymentType = data.lineItems[0]?.customPaymentType;

      const payload = {
        ...data.formData,
        paymentType: mainPaymentType,
        customPaymentType: mainPaymentType === 'OTHER' ? mainCustomPaymentType || undefined : undefined,
        minimumAmount: data.formData.minimumAmount ? parseFloat(data.formData.minimumAmount) : undefined,
        propertyLocation: data.formData.propertyLocation || undefined,
        isRecurring: !!firstRecurringItem,
        recurringFrequency: firstRecurringItem?.recurringFrequency || undefined,
        recurringCount: firstRecurringItem?.recurringCount ? parseInt(firstRecurringItem.recurringCount) : undefined,
        lineItems: data.lineItems.map(item => ({
          description: item.description,
          paymentType: item.paymentType || undefined,
          customPaymentType: item.paymentType === 'OTHER' ? item.customPaymentType || undefined : undefined,
          quantity: item.quantity ? parseInt(item.quantity) : 1,
          unitPrice: parseFloat(item.unitPrice) || 0,
        })),
      };
      return templateApi.create(payload);
    },
    onSuccess: () => {
      toast.success('Template created successfully');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { formData: typeof initialFormData; lineItems: LineItemFormData[] } }) => {
      // Get first recurring item for main recurring settings
      const firstRecurringItem = data.lineItems.find(item => item.isRecurring);
      const mainPaymentType = data.lineItems[0]?.paymentType || 'OTHER';
      const mainCustomPaymentType = data.lineItems[0]?.customPaymentType;

      const payload = {
        ...data.formData,
        paymentType: mainPaymentType,
        customPaymentType: mainPaymentType === 'OTHER' ? mainCustomPaymentType || undefined : undefined,
        minimumAmount: data.formData.minimumAmount ? parseFloat(data.formData.minimumAmount) : undefined,
        propertyLocation: data.formData.propertyLocation || undefined,
        isRecurring: !!firstRecurringItem,
        recurringFrequency: firstRecurringItem?.recurringFrequency || undefined,
        recurringCount: firstRecurringItem?.recurringCount ? parseInt(firstRecurringItem.recurringCount) : undefined,
        lineItems: data.lineItems.map(item => ({
          description: item.description,
          paymentType: item.paymentType || undefined,
          customPaymentType: item.paymentType === 'OTHER' ? item.customPaymentType || undefined : undefined,
          quantity: item.quantity ? parseInt(item.quantity) : 1,
          unitPrice: parseFloat(item.unitPrice) || 0,
        })),
      };
      return templateApi.update(id, payload);
    },
    onSuccess: () => {
      toast.success('Template updated successfully');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templateApi.delete(id),
    onSuccess: () => {
      toast.success('Template deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setDeleteTemplate(null);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => templateApi.toggleActive(id),
    onSuccess: () => {
      toast.success('Template status updated');
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    },
  });

  const openCreateModal = () => {
    setFormData(initialFormData);
    setLineItems([{ ...emptyLineItem }]);
    setExpandedItems([0]);
    setSelectedTemplate(null);
    setModalMode('create');
  };

  const openEditModal = (template: PaymentLinkTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      title: template.title,
      linkDescription: template.linkDescription || '',
      currency: template.currency,
      allowPartialPayment: template.allowPartialPayment,
      minimumAmount: template.minimumAmount?.toString() || '',
      propertyLocation: template.propertyLocation || '',
      sendEmailOnCreate: template.sendEmailOnCreate,
      sendReminderEmails: template.sendReminderEmails,
    });
    // Load existing line items with recurring info
    if (template.lineItems && template.lineItems.length > 0) {
      setLineItems(template.lineItems.map(item => ({
        description: item.description,
        paymentType: item.paymentType || template.paymentType || 'OTHER',
        customPaymentType: item.customPaymentType || template.customPaymentType || '',
        quantity: item.quantity?.toString() || '1',
        unitPrice: item.unitPrice?.toString() || '',
        isRecurring: template.isRecurring,
        recurringFrequency: template.recurringFrequency || '',
        recurringCount: template.recurringCount?.toString() || '',
      })));
    } else {
      // If no line items, create one from the template amount
      setLineItems([{
        description: '',
        paymentType: template.paymentType || 'OTHER',
        customPaymentType: template.customPaymentType || '',
        quantity: '1',
        unitPrice: template.amount?.toString() || '',
        isRecurring: template.isRecurring,
        recurringFrequency: template.recurringFrequency || '',
        recurringCount: template.recurringCount?.toString() || '',
      }]);
    }
    setExpandedItems([0]);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedTemplate(null);
    setFormData(initialFormData);
    setLineItems([{ ...emptyLineItem }]);
    setExpandedItems([0]);
  };

  const addLineItem = () => {
    const newIndex = lineItems.length;
    setLineItems([...lineItems, { ...emptyLineItem }]);
    setExpandedItems(prev => [...prev, newIndex]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
      setExpandedItems(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
    }
  };

  const updateLineItem = (index: number, field: keyof LineItemFormData, value: string | boolean) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  const toggleExpand = (index: number) => {
    setExpandedItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const calculateLineItemsTotal = () => {
    return lineItems.reduce((sum, item) => {
      const quantity = parseInt(item.quantity) || 1;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + quantity * unitPrice;
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (modalMode === 'create') {
      createMutation.mutate({ formData, lineItems });
    } else if (modalMode === 'edit' && selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate.id, data: { formData, lineItems } });
    }
  };

  const formatCurrency = (amount?: number, currency = 'PHP') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getPaymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      RESERVATION_FEE: 'Reservation',
      DOWN_PAYMENT: 'Down Payment',
      MONTHLY_AMORTIZATION: 'Monthly',
      SPOT_CASH: 'Spot Cash',
      BALANCE_PAYMENT: 'Balance',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const templates = data?.data as PaymentLinkTemplate[] | undefined;
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Payment Link Templates</h1>
          <p className="page-subtitle">Create and manage reusable payment link templates</p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={openCreateModal}>
          Create Template
        </Button>
      </div>

      {/* Search and filters */}
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by name or title..."
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="outline"
              leftIcon={<FunnelIcon className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <Select
                options={paymentTypeOptions}
                value={paymentTypeFilter}
                onChange={(e) => {
                  setPaymentTypeFilter(e.target.value);
                  setPage(1);
                }}
                placeholder="Filter by type"
              />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Templates list */}
      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-error">{getErrorMessage(error)}</p>
            </div>
          ) : !templates || templates.length === 0 ? (
            <EmptyState
              icon={<DocumentDuplicateIcon className="w-12 h-12" />}
              title="No templates found"
              description="Create your first template to speed up payment link creation"
              action={{
                label: 'Create Template',
                onClick: openCreateModal,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Template</th>
                      <th>Payment Type</th>
                      <th>Amount</th>
                      <th>Recurring</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {templates.map((template) => (
                      <tr key={template.id}>
                        <td>
                          <div className="min-w-[200px]">
                            <p className="font-medium text-gray-900">{template.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{template.title}</p>
                          </div>
                        </td>
                        <td>
                          <Badge variant="info">
                            {getPaymentTypeLabel(template.paymentType)}
                          </Badge>
                        </td>
                        <td className="text-sm text-gray-900">
                          {template.lineItems && template.lineItems.length > 0 ? (
                            <div>
                              <span>{formatCurrency(
                                template.lineItems.reduce((sum, item) => sum + (item.quantity || 1) * item.unitPrice, 0),
                                template.currency
                              )}</span>
                              <Badge variant="gray" className="ml-2">{template.lineItems.length} items</Badge>
                            </div>
                          ) : (
                            formatCurrency(template.amount, template.currency)
                          )}
                        </td>
                        <td>
                          {template.isRecurring ? (
                            <Badge variant="primary">
                              {template.recurringFrequency}
                              {template.recurringCount && ` x${template.recurringCount}`}
                            </Badge>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td>
                          <button
                            onClick={() => toggleActiveMutation.mutate(template.id)}
                            className="flex items-center gap-1.5"
                          >
                            {template.isActive ? (
                              <>
                                <CheckCircleIcon className="w-4 h-4 text-success" />
                                <span className="text-sm text-success">Active</span>
                              </>
                            ) : (
                              <>
                                <XCircleIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-400">Inactive</span>
                              </>
                            )}
                          </button>
                        </td>
                        <td className="text-sm text-gray-500">
                          {format(new Date(template.createdAt), 'MMM d, yyyy')}
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/admin/payment-links/new?template=${template.id}`)}
                              className="p-1.5 text-primary hover:text-primary-700 rounded"
                              title="Create payment link from template"
                            >
                              <LinkIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openEditModal(template)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                              title="Edit template"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTemplate(template)}
                              className="p-1.5 text-gray-400 hover:text-error rounded"
                              title="Delete template"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} templates
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={!!modalMode} onClose={closeModal} size="lg">
        <ModalHeader onClose={closeModal}>
          {modalMode === 'create' ? 'Create Template' : 'Edit Template'}
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <div className="space-y-6">
              {/* Template Info */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Template Information</h4>
                <div className="grid grid-cols-1 gap-4">
                  <Input
                    label="Template Name"
                    placeholder="e.g., Monthly Amortization - Niseko"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                  <Input
                    label="Description"
                    placeholder="Internal description for this template"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              {/* Payment Link Defaults */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Link Defaults</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Input
                      label="Default Title"
                      placeholder="e.g., Monthly Amortization - Unit {unit}"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <Select
                    label="Property Location"
                    options={locationOptions}
                    value={formData.propertyLocation}
                    onChange={(e) => setFormData({ ...formData, propertyLocation: e.target.value })}
                  />
                  <Select
                    label="Currency"
                    options={currencyOptions}
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  />
                </div>
              </div>

              {/* Line Items with Per-Item Recurring */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Items</h4>
                <p className="text-xs text-gray-500 mb-4">
                  Add items to this template. Each item can have its own payment type and recurring settings.
                </p>
                <div className="space-y-3">
                  {lineItems.map((item, index) => {
                    const isExpanded = expandedItems.includes(index);
                    const itemTotal = (parseInt(item.quantity) || 1) * (parseFloat(item.unitPrice) || 0);

                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        {/* Item Header - Always visible */}
                        <div
                          className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleExpand(index)}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                              Item {index + 1}
                            </span>
                            {item.description && (
                              <span className="text-sm text-gray-500">
                                - {item.description}
                              </span>
                            )}
                            {item.paymentType && (
                              <span className="text-xs px-2 py-0.5 bg-primary-100 text-primary-700 rounded">
                                {item.paymentType === 'OTHER' && item.customPaymentType
                                  ? item.customPaymentType
                                  : getPaymentTypeLabel(item.paymentType)}
                              </span>
                            )}
                            {item.isRecurring && item.recurringFrequency && (
                              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                {item.recurringFrequency}
                                {item.recurringCount && ` x${item.recurringCount}`}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {formData.currency} {itemTotal.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>
                            {lineItems.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeLineItem(index);
                                }}
                                className="text-red-500 hover:text-red-700 p-1"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            )}
                            {isExpanded ? (
                              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </div>

                        {/* Item Details - Expandable */}
                        {isExpanded && (
                          <div className="p-4 space-y-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <Input
                                label="Description"
                                placeholder="e.g., Reservation Fee"
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                required
                              />
                              <div>
                                <Select
                                  label="Payment Type"
                                  options={paymentTypeSelectOptions}
                                  value={item.paymentType}
                                  onChange={(e) => {
                                    updateLineItem(index, 'paymentType', e.target.value);
                                    if (e.target.value !== 'OTHER') {
                                      updateLineItem(index, 'customPaymentType', '');
                                    }
                                  }}
                                />
                                {item.paymentType === 'OTHER' && (
                                  <Input
                                    placeholder="Enter custom payment type"
                                    value={item.customPaymentType}
                                    onChange={(e) => updateLineItem(index, 'customPaymentType', e.target.value)}
                                    className="mt-2"
                                  />
                                )}
                              </div>
                              <Input
                                label="Quantity"
                                type="number"
                                min={1}
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                              />
                              <Input
                                label="Unit Price"
                                type="number"
                                min={0}
                                step="0.01"
                                placeholder="0.00"
                                value={item.unitPrice}
                                onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                                required
                              />
                            </div>

                            {/* Recurring options per item */}
                            <div className="pt-3 border-t border-gray-100">
                              <label className="flex items-center gap-2 mb-3">
                                <input
                                  type="checkbox"
                                  checked={item.isRecurring}
                                  onChange={(e) => updateLineItem(index, 'isRecurring', e.target.checked)}
                                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                                />
                                <span className="text-sm font-medium text-gray-700">Make this a recurring payment</span>
                              </label>

                              {item.isRecurring && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-6">
                                  <Select
                                    label="Frequency"
                                    options={recurringFrequencyOptions}
                                    value={item.recurringFrequency}
                                    onChange={(e) => updateLineItem(index, 'recurringFrequency', e.target.value)}
                                  />
                                  <Input
                                    label="Number of Payments"
                                    type="number"
                                    min={1}
                                    placeholder="e.g., 12"
                                    value={item.recurringCount}
                                    onChange={(e) => updateLineItem(index, 'recurringCount', e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLineItem}
                    leftIcon={<PlusIcon className="w-4 h-4" />}
                  >
                    Add Item
                  </Button>

                  {lineItems.length > 0 && (
                    <div className="flex justify-end pt-3 border-t">
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-lg font-bold text-gray-900">
                          {formData.currency} {calculateLineItemsTotal().toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Additional Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="allowPartialPayment"
                      checked={formData.allowPartialPayment}
                      onChange={(e) => setFormData({ ...formData, allowPartialPayment: e.target.checked })}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="allowPartialPayment" className="text-sm text-gray-700">
                      Allow Partial Payment
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="sendEmailOnCreate"
                      checked={formData.sendEmailOnCreate}
                      onChange={(e) => setFormData({ ...formData, sendEmailOnCreate: e.target.checked })}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="sendEmailOnCreate" className="text-sm text-gray-700">
                      Send Email on Create
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="sendReminderEmails"
                      checked={formData.sendReminderEmails}
                      onChange={(e) => setFormData({ ...formData, sendReminderEmails: e.target.checked })}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <label htmlFor="sendReminderEmails" className="text-sm text-gray-700">
                      Send Reminder Emails
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={closeModal} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {modalMode === 'create' ? 'Create Template' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTemplate} onClose={() => setDeleteTemplate(null)} size="sm">
        <ModalHeader onClose={() => setDeleteTemplate(null)}>Delete Template</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete{' '}
            <span className="font-medium">"{deleteTemplate?.name}"</span>? This action cannot be
            undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteTemplate(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={deleteMutation.isPending}
            onClick={() => deleteTemplate && deleteMutation.mutate(deleteTemplate.id)}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
